import { IconAlert, IconCircleCheck, IconInfo, IconOctagonX, IconX } from '@developerz/ui';
import { createSignal, type JSX, onCleanup, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { t } from '../i18n';
import type { ToastAction } from '../lib/toast';
import { Button, buttonClass } from './ui/Button';

// Transient notice pinned bottom-right (spec §3.14): the canvas panel floating
// on the one overlay shadow, its tone carried by the icon's hue. Controlled:
// the caller owns the message signal and the auto-dismiss timer. Close plays a
// short exit animation before notifying the caller; a caller-driven
// message → null change still unmounts instantly. The fade belongs to the
// MESSAGE it closed: a new notice landing mid-fade is neither faded nor
// dismissed by the old one's timer.

type ToastTone = 'success' | 'info' | 'error' | 'warning';

const ICONS: Record<ToastTone, (props: { class?: string; size?: string }) => JSX.Element> = {
  success: IconCircleCheck,
  info: IconInfo,
  error: IconOctagonX,
  warning: IconAlert,
};

export function Toast(props: {
  readonly message: string | null;
  readonly tone?: ToastTone;
  /** Optional action (e.g. Undo) — runs once on click, then dismisses. */
  readonly action?: ToastAction | undefined;
  readonly onDismiss: () => void;
}): JSX.Element {
  // The message being faded out, or null. `leaving()` holds only while it is
  // still the one on screen, so a replacement enters visible.
  const [closing, setClosing] = createSignal<string | null>(null);
  const leaving = (): boolean => closing() !== null && closing() === props.message;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const tone = (): ToastTone => props.tone ?? 'success';
  const dismiss = (): void => {
    if (leaving()) return;
    const closed = props.message;
    setClosing(closed);
    clearTimeout(timer);
    timer = setTimeout(() => {
      setClosing(null);
      if (props.message === closed) props.onDismiss();
    }, 160);
  };
  // Reuses `dismiss`'s own `leaving` guard rather than a second one, so a
  // double-click can't run the action twice — the SAME single timer this
  // toast already owns (started by `dismiss` above) is what clears it either
  // way; the action adds no timer of its own.
  //
  // `dismiss()` runs BEFORE `run()`, not after: `dismiss` captures
  // `props.message` at the instant it runs, so it must capture and latch
  // `leaving()` for THIS toast before the action gets a chance to run and
  // synchronously post a new message (e.g. an Undo action that raises its
  // own confirmation toast). Calling `run()` first would let a synchronous
  // new message land ahead of the capture, so `dismiss` closes the new
  // message instead of the one that was actually clicked — and a throwing
  // `run()` would skip `dismiss()` entirely, leaving `leaving()` unlatched
  // for a second click to run the action again.
  const runAction = (): void => {
    if (leaving()) return;
    dismiss();
    props.action?.run();
  };
  onCleanup(() => clearTimeout(timer));
  return (
    <Show when={props.message}>
      <div
        class={`toast toast--${tone()}`}
        classList={{ 'toast--leaving': leaving() }}
        role="status"
      >
        <Dynamic component={ICONS[tone()]} class="toast__icon" />
        <span class="toast__msg">{props.message}</span>
        <Show when={props.action}>
          {(action) => (
            <button
              type="button"
              class={`${buttonClass({ variant: 'ghost', size: 'sm' })} toast__action`}
              onClick={runAction}
            >
              {action().label}
            </button>
          )}
        </Show>
        <Button variant="ghost" size="icon-sm" aria-label={t('toast.dismiss')} onClick={dismiss}>
          <IconX size="1rem" />
        </Button>
      </div>
    </Show>
  );
}
